"use client";

import React from "react";
import { Card } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import { Public, Checklist, PersonSearch, ContactMail } from "@mui/icons-material";
import { motion } from "framer-motion";
import { FlowingSOLALogo } from "./FlowingSOLALogo";

const sampleQuestions = [
  {
    icon: Public,
    heading: "Market mapping",
    description: "Identify a list of target companies and outreach strategy",
    prompt:
      "Help me with market mapping. Use my base prompt (if populated) as context, then ask me clarifying questions about industry, location, and the type of recruitment I focus on. Review a few of my recent candidates to understand their skills and experience. Use this to identify 20 target companies I could reach out to with the goal of winning them as clients and placing candidates there. For each company identify key contacts and recommend outreach approaches.",
    accent: colors.action.product,
    accentLight: colors.action.productLight,
  },
  {
    icon: Checklist,
    heading: "Shortlist",
    description: "Given a job description, identify good-fit candidates",
    prompt:
      "Help me shortlist candidates. Ask me for a job description, or the name of a Job from my Jobs data (note: Jobs are a distinct entity from Floats — search the Jobs data, not Floats). Once I provide the job, identify candidates from my Candidates data who would be a strong fit for that role, and explain why for each.",
    accent: colors.signal.info,
    accentLight: colors.signal.infoLight,
  },
  {
    icon: PersonSearch,
    heading: "Marketing a candidate",
    description: "Find live roles for a candidate and recommend outreach strategy",
    prompt:
      "Help me market a candidate. Ask me for a candidate name, or to upload a CV. If I upload a CV, import the candidate and save them in my Candidates. Review the candidate's location, skills, and experience, then search the web for live roles that would be a good fit. Explain why each role fits and provide outreach recommendations.",
    accent: colors.signal.success,
    accentLight: colors.signal.successLight,
  },
  {
    icon: ContactMail,
    heading: "Find hiring managers",
    description: "Identify key contacts and hiring managers relevant to your agency",
    prompt:
      "Help me find hiring managers. Consult my base prompt (if populated) for context, then ask me for the relevant location, industry, and titles of the people I want to reach (e.g. CTO, VP Engineering, Head of Talent). IMPORTANT: I am NOT looking for advertised job openings or roles to apply to. I am looking for real people currently working in those hiring-decision roles at target companies — they are prospective clients I want to win so I can place candidates into their teams. Search the web (do not search my local Contacts data) for named individuals currently holding those titles at relevant companies, enrich their professional details from public sources (LinkedIn, company sites, etc.), and prompt me to save them as Contacts.",
    accent: colors.action.accent,
    accentLight: primitives.pink[50],
  },
];

interface GreetingProps {
  onQuestionClick?: (question: string) => void;
}

export function Greeting({ onQuestionClick }: GreetingProps) {
  return (
    <div style={{ textAlign: "center", width: "100%", paddingTop: spacing[4], paddingBottom: spacing[4] }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div style={{ marginBottom: spacing[3], display: "flex", justifyContent: "center" }}>
          <FlowingSOLALogo size={64} />
        </div>
        <h1
          style={{
            ...textStyles.h1,
            fontWeight: 600,
            marginBottom: spacing[3],
            background: `linear-gradient(135deg, ${colors.action.product} 0%, ${colors.action.accent} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Welcome to SOLA
        </h1>
        <p
          style={{
            ...textStyles.body1,
            color: colors.text.muted,
            marginBottom: spacing[5],
            maxWidth: 440,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Your AI-powered recruiting copilot. Search candidates, build floats,
          analyze pipelines, and get real-time market intelligence.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        style={{
          ...textStyles.caption,
          color: colors.text.disabled,
          fontWeight: 500,
          textTransform: "uppercase" as const,
          letterSpacing: "0.06em",
          marginTop: spacing[3],
          marginBottom: spacing[3],
        }}
      >
        Try asking
      </motion.div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: spacing[3],
        }}
      >
        {sampleQuestions.map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02, y: -2 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            style={{ height: "100%" }}
          >
            <Card
              variant="default"
              padding="sm"
              interactive
              onClick={() => onQuestionClick?.(item.prompt)}
              style={{ borderRadius: radius.md, height: "100%" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: spacing[3],
                }}
              >
                <div
                  style={{
                    padding: spacing[2],
                    borderRadius: radius.md,
                    backgroundColor: item.accentLight,
                    color: item.accent,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <item.icon style={{ fontSize: 20 }} />
                </div>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <span
                    style={{
                      ...textStyles.body2,
                      display: "block",
                      fontWeight: 600,
                      marginBottom: spacing[1],
                      color: colors.text.primary,
                    }}
                  >
                    {item.heading}
                  </span>
                  <span
                    style={{
                      ...textStyles.caption,
                      color: colors.text.muted,
                    }}
                  >
                    {item.description}
                  </span>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
